import { useEffect, useRef, useState } from "react";
import { updateGoalProgress } from "wasp/client/operations";
import useTranslation from "../../client/hooks/useTranslation";
import type { GoalWithHistory } from "../../trading-goals/operations";
import { useLiteData } from "../data/LiteDataProvider";
import { useLiteDiscipline, type LiteRoutine } from "../data/useLiteDiscipline";
import { PageBody, PageHeader } from "../shell/LiteLayout";
import { Empty, cardCls } from "../ui/ui";
import { GoalDrawer } from "./discipline/GoalDrawer";
import { GoalsCard } from "./discipline/GoalsCard";
import { RoutineDrawer } from "./discipline/RoutineDrawer";
import { RoutineTodayCard } from "./discipline/RoutineTodayCard";

/** Discipline du Lite : la routine du jour à cocher, les objectifs en cours. Pas de filtres. */
export default function LiteDisciplinePage() {
  const t = useTranslation().lite;
  const tt = useTranslation();
  const { fmt } = useLiteData();
  const d = useLiteDiscipline();
  // L'objet sélectionné vit dans l'état : les tiroirs réinitialisent leur formulaire quand la référence change.
  const [routineDrawer, setRoutineDrawer] = useState<{ open: boolean; routine: LiteRoutine | null }>({ open: false, routine: null });
  const [goalDrawer, setGoalDrawer] = useState<{ open: boolean; goal: GoalWithHistory | null }>({ open: false, goal: null });

  // Progression recalculée à l'ouverture, comme en Advanced (Wasp invalide ensuite getGoalsByUser).
  const refreshed = useRef(false);
  useEffect(() => {
    if (refreshed.current) return;
    refreshed.current = true;
    updateGoalProgress().catch(() => { /* la liste reste lisible avec les valeurs en base */ });
  }, []);

  return (
    <>
      <PageHeader title={t.discipline.title} subtitle={fmt.dateLong(new Date())} filters={false} />
      <PageBody>
        {d.loading ? (
          <div className={cardCls}><Empty title={t.common.loading} /></div>
        ) : d.error ? (
          <div className={cardCls}><Empty title={tt.common.error} /></div>
        ) : (
          <div className="grid xl:grid-cols-2 gap-5 items-start">
            <RoutineTodayCard
              groups={d.today.groups}
              total={d.today.total}
              streak={d.streak}
              onAdd={() => setRoutineDrawer({ open: true, routine: null })}
              onEdit={r => setRoutineDrawer({ open: true, routine: r })}
            />
            <GoalsCard
              active={d.active}
              completed={d.completed}
              onAdd={() => setGoalDrawer({ open: true, goal: null })}
              onEdit={g => setGoalDrawer({ open: true, goal: g })}
            />
          </div>
        )}
      </PageBody>
      <RoutineDrawer open={routineDrawer.open} routine={routineDrawer.routine} onClose={() => setRoutineDrawer(s => ({ ...s, open: false }))} />
      <GoalDrawer open={goalDrawer.open} goal={goalDrawer.goal} onClose={() => setGoalDrawer(s => ({ ...s, open: false }))} />
    </>
  );
}
